/**
 * HTTP wire contracts for the `/employees` module.
 *
 * Every route's request + response body is typed here so both the server
 * and the client import the same Zod schemas. Keeps drift impossible: a
 * breaking change to a response shape appears as a TS error on the
 * consumer side at commit time.
 */
import { z } from 'zod';

import {
  CursorPaginationQuerySchema,
  CursorPaginationResponseSchema,
} from '../common/pagination.js';

import {
  EmployeeAddressSchema,
  NewEmployeeAddressSchema,
  UpdateEmployeeAddressSchema,
} from './employee-address.js';
import {
  EmployeeDependentSchema,
  NewEmployeeDependentSchema,
  UpdateEmployeeDependentSchema,
} from './employee-dependent.js';
import {
  EmployeeEmergencyContactSchema,
  NewEmployeeEmergencyContactSchema,
  UpdateEmployeeEmergencyContactSchema,
} from './employee-emergency-contact.js';
import {
  EmployeeSchema,
  EmploymentStatusSchema,
  EmploymentTypeSchema,
  NewEmployeeSchema,
  UpdateEmployeeSchema,
} from './employee.js';

// ---------------------------------------------------------------------------
// POST /employees
// ---------------------------------------------------------------------------

export const CreateEmployeeInputSchema = NewEmployeeSchema;
export type CreateEmployeeInput = z.infer<typeof CreateEmployeeInputSchema>;

export const CreateEmployeeResponseSchema = z.object({
  employee: EmployeeSchema,
});
export type CreateEmployeeResponse = z.infer<typeof CreateEmployeeResponseSchema>;

// ---------------------------------------------------------------------------
// GET /employees
// ---------------------------------------------------------------------------

/**
 * Filterable list. `search` matches case-insensitively against
 * `display_name`, `first_name`, `last_name`, and `work_email`. `cursor` +
 * `limit` come from the shared `CursorPaginationQuerySchema` — the cursor
 * is an opaque base64url-encoded row key owned by the server.
 */
export const ListEmployeesQuerySchema = CursorPaginationQuerySchema.extend({
  status: EmploymentStatusSchema.optional(),
  employmentType: EmploymentTypeSchema.optional(),
  departmentId: z.string().uuid().optional(),
  search: z.string().min(1).max(200).optional(),
});
export type ListEmployeesQuery = z.infer<typeof ListEmployeesQuerySchema>;

export const ListEmployeesResponseSchema = CursorPaginationResponseSchema(EmployeeSchema);
export type ListEmployeesResponse = z.infer<typeof ListEmployeesResponseSchema>;

// ---------------------------------------------------------------------------
// GET /employees/:id
// ---------------------------------------------------------------------------

/**
 * Embedded response — the core row plus every sub-collection. Saves the
 * client an N+1 round-trip when rendering the profile page.
 */
export const EmployeeDetailResponseSchema = z.object({
  employee: EmployeeSchema,
  addresses: z.array(EmployeeAddressSchema),
  emergencyContacts: z.array(EmployeeEmergencyContactSchema),
  dependents: z.array(EmployeeDependentSchema),
});
export type EmployeeDetailResponse = z.infer<typeof EmployeeDetailResponseSchema>;

// ---------------------------------------------------------------------------
// PATCH /employees/:id
// ---------------------------------------------------------------------------

export const UpdateEmployeeInputSchema = UpdateEmployeeSchema;
export type UpdateEmployeeInput = z.infer<typeof UpdateEmployeeInputSchema>;

export const UpdateEmployeeResponseSchema = z.object({
  employee: EmployeeSchema,
});
export type UpdateEmployeeResponse = z.infer<typeof UpdateEmployeeResponseSchema>;

// ---------------------------------------------------------------------------
// DELETE /employees/:id
// ---------------------------------------------------------------------------

export const DeleteEmployeeResponseSchema = z.object({
  id: z.string().uuid(),
  deletedAt: z.string().datetime({ offset: true }),
});
export type DeleteEmployeeResponse = z.infer<typeof DeleteEmployeeResponseSchema>;

// ---------------------------------------------------------------------------
// Addresses
// ---------------------------------------------------------------------------

export const CreateEmployeeAddressInputSchema = NewEmployeeAddressSchema;
export type CreateEmployeeAddressInput = z.infer<typeof CreateEmployeeAddressInputSchema>;

export const UpdateEmployeeAddressInputSchema = UpdateEmployeeAddressSchema;
export type UpdateEmployeeAddressInput = z.infer<typeof UpdateEmployeeAddressInputSchema>;

export const EmployeeAddressResponseSchema = z.object({
  address: EmployeeAddressSchema,
});
export type EmployeeAddressResponse = z.infer<typeof EmployeeAddressResponseSchema>;

// ---------------------------------------------------------------------------
// Emergency contacts
// ---------------------------------------------------------------------------

export const CreateEmergencyContactInputSchema = NewEmployeeEmergencyContactSchema;
export type CreateEmergencyContactInput = z.infer<typeof CreateEmergencyContactInputSchema>;

export const UpdateEmergencyContactInputSchema = UpdateEmployeeEmergencyContactSchema;
export type UpdateEmergencyContactInput = z.infer<typeof UpdateEmergencyContactInputSchema>;

export const EmergencyContactResponseSchema = z.object({
  contact: EmployeeEmergencyContactSchema,
});
export type EmergencyContactResponse = z.infer<typeof EmergencyContactResponseSchema>;

// ---------------------------------------------------------------------------
// Dependents
// ---------------------------------------------------------------------------

export const CreateDependentInputSchema = NewEmployeeDependentSchema;
export type CreateDependentInput = z.infer<typeof CreateDependentInputSchema>;

export const UpdateDependentInputSchema = UpdateEmployeeDependentSchema;
export type UpdateDependentInput = z.infer<typeof UpdateDependentInputSchema>;

export const DependentResponseSchema = z.object({
  dependent: EmployeeDependentSchema,
});
export type DependentResponse = z.infer<typeof DependentResponseSchema>;
